import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '..'
import { spotify, apple, applePlayer, spotifyPlayer, umgHlsPlayer } from '@/services'
import { PlayerStatus } from '@/ts/interfaces/player-status'
import { PlaybackState } from 'umg-cms-js-sdk/src/player/types'
import { defaultPlaybackState, PlayerDevice, ServicePlayer } from 'umg-cms-js-sdk/src/player/service_player'
import { ON_TRACK_QUEU } from '@/constants/events'
import { Track } from '@/ts/interfaces/track'
import { EventBus } from '@/event-bus'
import { cms } from '@/cms'
import { UmgHlsPlayer } from 'umg-cms-js-sdk/src/player/umg_hls_player'

type PlayerService = 'spotify'|'apple'|'umg_hls'

const getPlayer = (service: PlayerService): ServicePlayer => {
  switch (service) {
    case 'spotify': {
      return spotifyPlayer
    }
    case 'apple': {
      return applePlayer
    } case 'umg_hls': {
      return umgHlsPlayer
    }
    default: {
      throw new Error(`Unknown service ${service}`)
    }
  }
}

interface PlayerState {
  started: boolean // Initial play call is done
  playbackState: PlaybackState, // sdk player state
  devices: PlayerDevice[], // sdk player devices
  isMediaSupportError: boolean,
  initDone: boolean,
  isPaused: boolean

}

const state: PlayerState = {
  started: false,
  playbackState: defaultPlaybackState(),
  devices: [],
  isMediaSupportError: false,
  initDone: false,
  isPaused: false // on start, player is not paused

}

const mutations: MutationTree<PlayerState> = {
  SET_PLAYBACK_STATE(state: PlayerState, playbackState: PlaybackState) {
    state.playbackState = playbackState
    // Only set started once, but don't unset it. Prevents start button from showing again
    state.started = state.started || !playbackState.isPaused
  },
  SET_PLAYER_DEVICES(state: PlayerState, devices: PlayerDevice[]) {
    state.devices = devices
  },
  SET_PLAYER_ACTIVE_DEVICE(state: PlayerState, deviceId: string) {
    // Find the device in array and set it as active
    for (const device of state.devices) {
      device.active = device.id === deviceId
    }
  },
  SET_IS_MEDIA_SUPPORT_ERROR(state: PlayerState, value: boolean) {
    state.isMediaSupportError = value
  },
  SET_INIT_DONE(state: PlayerState, value: boolean) {
    state.initDone = value
  },
  SET_IS_MANUALLY_PAUSED(state: PlayerState, value: boolean) {
    state.isPaused = value
  }
}

const actions: ActionTree<PlayerState, RootState> = {
  async PLAYER_SERVICES_INIT(ctx: ActionContext<PlayerState, RootState>) {
    // Init service clients, loads the SDKs, etc
    if (ctx.getters.partyServices.includes('apple')) {
      await apple.init()
    }
    if (ctx.getters.partyServices.includes('spotify')) {
      await spotify.init({
        getToken: async () => {
          await ctx.dispatch('USER_LOAD_SERVICE_TOKEN_IF_NEEDED')
          return ctx.getters.userServiceToken
        }
      })
    }
    // No init here for umg_hls, video element is set in component
    document.addEventListener('umg:player:initializationError', () => {
      ctx.commit('SET_IS_MEDIA_SUPPORT_ERROR', true)
    })
  },
  async PLAYER_INIT(ctx: ActionContext<PlayerState, RootState>) {
    if (ctx.state.initDone) {
      return
    }
    // Init the player if user is logged in
    if (!ctx.getters.userIsAuthenticated) {
      return
    }
    const player = getPlayer(ctx.getters.playerService)

    document.addEventListener('umg:player:playbackStateChanged', (e: CustomEvent<PlaybackState>) => {
      console.debug('playbackStateChanged:', e.detail)
      ctx.commit('SET_PLAYBACK_STATE', e.detail)
    })

    EventBus.$on(ON_TRACK_QUEU, async (event: { tracks: Track[] }) => {
      await ctx.dispatch('PLAYER_ENQUEUE_TRACKS', event.tracks)
      // TODO: Tracks in player module
      ctx.commit('PARTY_SET_TRACKS', event.tracks)
    })

    // Is player instance of UmgHlsPlayer?
    if (player instanceof UmgHlsPlayer) {
      player.changeCallback(cms.cmsEnvironment === 'production' ? 'prod' : 'dev')
    }

    // set callback

    ctx.commit('SET_INIT_DONE', true)
  },
  async PLAYER_ENQUEUE_TRACKS(ctx: ActionContext<PlayerState, RootState>, tracks: Track[]) {
    const service: PlayerService = ctx.getters.playerService
    if (!service) {
      return
    }
    try {
      await getPlayer(service).enqueueTracks(
        tracks.map((t) => t.service_id[service])
      )
    } catch (e) {
      if ((e as Error).message.includes('One or more items could not be resolved')) {
        console.error('Apple failed to enqueue some tracks, going on without them', e)
      } else {
        throw e
      }
    }
  },
  async PLAYER_PLAY(ctx: ActionContext<PlayerState, RootState>) {
    // E.g. clicking start
    const service: PlayerService = ctx.getters.playerService
    const playerStatus: PlayerStatus|null = ctx.getters.partyStatus
    if (!service || !playerStatus) {
      console.warn('Attempted to play without a service or player status')
      return
    }

    const player = getPlayer(service)
    await player.init()
    await player.playTrack(playerStatus.track.service_id[service], { positionMs: playerStatus.progress_ms })
    await ctx.dispatch('PLAYER_LOAD_DEVICES')
    ctx.commit('SET_IS_MANUALLY_PAUSED', false)
  },
  async PLAYER_SYNC(ctx: ActionContext<PlayerState, RootState>) {
    // Load the current status fresh from server and play
    await ctx.dispatch('PARTY_STATUS')
    await ctx.dispatch('PLAYER_PLAY')
  },
  async PLAYER_ON_TRACK_CHANGE(ctx: ActionContext<PlayerState, RootState>, status: PlayerStatus) {
    // Called when track changes (in response to websocket event)
    ctx.commit('PARTY_SET_STATUS', status)
    if (ctx.state.isPaused) {
      // Don't start automatically if manually paused
      return
    }
    await ctx.dispatch('PLAYER_PLAY')
  },
  async PLAYER_PAUSE(ctx: ActionContext<PlayerState, RootState>) {
    const service = ctx.getters.playerService
    if (!service) {
      console.warn('Attempted to pause without a service')
      return
    }

    await getPlayer(service).pause()
    ctx.commit('SET_IS_MANUALLY_PAUSED', true)
  },
  async PLAYER_LOAD_DEVICES(ctx: ActionContext<PlayerState, RootState>) {
    const service = ctx.getters.playerService
    if (!service) {
      console.warn('Attempted to load devices without a service')
      return
    }
    ctx.commit('SET_PLAYER_DEVICES', await getPlayer(service).getDevices())
  },
  async PLAYER_CHANGE_DEVICE(ctx: ActionContext<PlayerState, RootState>, deviceId: string) {
    const service = ctx.getters.playerService
    if (!service) {
      console.warn('Attempted to change device without a service')
      return
    }
    await getPlayer(service).setDevice(deviceId)
    ctx.commit('SET_PLAYER_ACTIVE_DEVICE', deviceId)
  },
  async PLAYER_SET_VOLUME(ctx: ActionContext<PlayerState, RootState>, volume: number) {
    const service = ctx.getters.playerService
    if (!service) {
      console.warn('Attempted to set volume without a service')
      return
    }
    await getPlayer(service).setVolume(volume)
  }
}

const getters: GetterTree<PlayerState, RootState> = {
  playerService: (_state: PlayerState, getters: any, _rootState: RootState, rootGetters: any) => {
    const userService = rootGetters.userService
    const partyServices = rootGetters.partyServices
    // Video streaming is the only player option when it's enabled
    if (partyServices.includes('umg_hls')) {
      return 'umg_hls'
    }
    return userService
  },
  playerIsPlaying: (state: PlayerState) => {
    return !state.playbackState.isPaused
  },
  playerDevices: (state: PlayerState) => {
    return state.devices
  },
  playerActiveDevice: (state: PlayerState): PlayerDevice | null => {
    return state.devices.find((d) => d.active) || null
  },
  playerIsLoading: (state: PlayerState) => {
    return state.playbackState.isLoading
  },
  playerVolume: (state: PlayerState) => {
    return state.playbackState.volume
  },
  playerSupportsVolume: (state: PlayerState) => {
    return state.playbackState.supportsVolume
  },
  playerSupportsDevices: (_state: PlayerState, _getters: any, _rootState: RootState, rootGetters: any) => {
    return getPlayer(rootGetters.playerService).supportsDevices
  },
  playerIsMediaSupportError: (state: PlayerState): boolean => {
    return state.isMediaSupportError
  },
  playerIsStarted: (state: PlayerState): boolean => {
    return state.started
  }

}

export const playerModule: Module<PlayerState, RootState> = {
  state, actions, mutations, getters
}
